import { ProgressIndicator, RadioButton, Text } from '@oneaudi/unified-web-components';
import { htmlToText } from 'html-to-text';
import { ReactNode } from 'react';
import { useI18n } from '~/app/i18n/i18n';
import { useMarketConfig, VATConfig } from '~/app/market-config';
import { useDebugMode } from '~/app/utils/debug-mode';
import { Prices } from '~/components/prices';
import { CardContent } from '~/components/selection-card-new/card-content';
import { SelectionCardStyles } from '~/components/selection-card-new/styles/selection-card-styles';
import { PriceOrError } from '~/graphql/mapper';
import { CurrencyFormatterDisplayContext } from '~/hooks/use-currency-formatter';
import { DATA_TEST_ID } from '~/test/data-test-id';

type CardItemInfo = {
	name: string;
	selected: boolean;
	loading: boolean;
	readonly: boolean;
	standard: boolean;
	onClick: () => void;
	price?: {
		totalPrice?: PriceOrError;
		monthlyRate?: number | undefined;
		trimlineAndEngine: VATConfig;
		displayContext?: CurrencyFormatterDisplayContext | undefined;
	};
	incentives?: ReactNode;
	prCode?: string;
	year?: string;
	debugCode?: string;
	onDescriptionToggle: (value: boolean) => void;
};

export const TrimlineSelectionCard = ({
	item: {
		name,
		selected,
		readonly,
		debugCode,
		loading,
		price,
		standard,
		onClick,
		incentives,
		prCode,
		year,
		onDescriptionToggle,
	},
	textLinks,
	children,
	id,
}: {
	item: CardItemInfo;
	children?: ReactNode;
	textLinks?: ReactNode;
	id: string;
}) => {
	const { i18n } = useI18n();
	const { isDebug } = useDebugMode();
	const { config } = useMarketConfig();
	const code = `[${debugCode ?? `${prCode}-${year}`}]`;
	const displayName = isDebug ? `${name} ${code}` : name;

	return (
		<SelectionCardStyles
			id={id}
			isDebug={isDebug}
			hasTechData={config.hasTechnicalDataEnabled}
			data-testid={DATA_TEST_ID.SELECTION_CARD_CONTAINER}
			className={`${readonly ? 'is-readonly' : ''} ${selected ? 'is-selected' : ''}`}>
			<div className="selection-card-content">
				<div className="selection-card-header">
					<div className="selection-card-title">
						<RadioButton
							label={htmlToText(displayName)}
							name=""
							onChange={() => ({})}
							checked={selected}
							id=""
							value=""
							className="top-item"
							data-testid={DATA_TEST_ID.SELECTION_CARD_CHECKBOX}
							aria-describedby={`${id}-price-information`}
							onClick={onClick}
						/>
						{loading ? (
							<ProgressIndicator className="loading-item" variant={'spinner'} type={'infinite'} />
						) : null}
					</div>
					{incentives}
					{price &&
						(standard ? (
							<Text
								id={`${id}-price-information`}
								tag="p"
								variant={{ type: 'copy', order: '2', style: 'normal' }}>
								{i18n('ngc.common.standard')}
							</Text>
						) : (
							<Prices
								id={`${id}-price-information`}
								totalPrice={price.totalPrice}
								monthlyRateOverride={price.monthlyRate}
								vatConfig={price.trimlineAndEngine}
								displayContext={price.displayContext}
							/>
						))}
				</div>
				<CardContent
					onDescriptionToggle={onDescriptionToggle}
					alwaysVisible={selected}
					linksSection={textLinks}>
					{children}
				</CardContent>
			</div>
		</SelectionCardStyles>
	);
};
